import { isTruthy } from '@sama/js-core';
import { BufferGeometry, Points } from 'three';
import {
  CloudShaderMaterial,
  setUniformsForColorMode,
} from '../../../materials/cloud/cloud-shader-material';
import { setRegionOfInterestUniforms } from '../../../materials/cloud/shaders/color/modes/region-of-interest';
import { setViewBoxHighlightUniforms } from '../../../materials/cloud/shaders/color/modes/viewbox-highlight';
import { setSubpointMeasurementModeUniforms } from '../../../materials/cloud/shaders/subpoint-measurement-mode';
import { ProjectionCamera } from '../../../renderers/projection/projection-camera';
import { PointCloudContentState } from '../../../state/point-cloud-content-state';
import { CloudAttributes } from '../../../utils/kd-tree/cloud-attributes.service';
import { assignProjectionDefines } from '../../../utils/materials/material-utils';
import { isRenderingWithRegionOfInterest } from '../../../utils/region-of-interest-utils';
import { getMaterialParams } from '../../../utils/timestamp-compensation-utils';
import {
  isRenderingWithViewBoxHighlight,
  updateMaterialForHighlight,
  updateSplitViewOutputForHighlight,
} from '../../../utils/viewbox-highlight-utils';
import {
  getAllRendererLayers,
  getAllRendererLayersExcept,
  RendererLayer,
  setRendererLayers,
} from '../../render-utils';
import {
  AbstractMeshHelper,
  ConfigureMeshesConfiguration,
} from '../abstract-mesh-helper';
import { updateGeometryAttributes } from './point-cloud-utils';

export class PointCloudHelper extends AbstractMeshHelper {
  cloud: Points<BufferGeometry, CloudShaderMaterial>;

  private splitViewCamera = new ProjectionCamera();

  constructor(
    private frameNumber: number,
    attributes: CloudAttributes,
  ) {
    super();
    this.cloud = new Points(new BufferGeometry());
    this.cloud.name = `cloud-${frameNumber}`;

    updateGeometryAttributes(this.cloud.geometry, attributes);
  }

  override getMeshes() {
    return [this.cloud];
  }

  override dispose() {
    this.cloud.geometry.dispose();
  }

  protected override updateRendererLayers(state: PointCloudContentState) {
    let layers = getAllRendererLayers();
    if (!state.render.projectionSettings.showClouds) {
      layers = getAllRendererLayersExcept(RendererLayer.Projection);
    }

    setRendererLayers(layers, this.cloud);
  }

  protected override configureMeshes(config: ConfigureMeshesConfiguration) {
    const { state } = config;
    this.setMaterial(config);
    this.setMotionCompensationParams(config);
    this.setDensity(state);
    this.setPointSize(state);
    this.setHideGround(state.render.hideGround);
    this.setViewBoxHighlight(config);
    this.setRegionOfInterest(config);
  }

  private setMaterial(config: ConfigureMeshesConfiguration) {
    const { state, materialService } = config;
    const name =
      state.render.frameNumber === this.frameNumber ? 'current' : 'enhanced';

    const material = materialService.get(CloudShaderMaterial, { name });

    const useSubpointHeightMode = isTruthy(
      state.render.subpointHeightMeasurement?.active,
    );
    material.setUniform('useSubPointMeasurementMode', useSubpointHeightMode);
    if (useSubpointHeightMode) {
      setSubpointMeasurementModeUniforms(material, state);
      material.setUniform('useSubPointMeasurementMode', true);
    } else {
      const { pointColorMode } = state.render.layerSettings;
      setUniformsForColorMode(pointColorMode, state, material);
    }

    this.cloud.material = material;
  }

  private setMotionCompensationParams({
    state,
    outputName,
  }: ConfigureMeshesConfiguration) {
    if (state.output.meta.enable_motion_compensation) {
      const { compensatedCuboids, compensatedCuboidCount } = getMaterialParams(
        state,
        outputName,
      );
      this.cloud.material.setUniform('compensatedCuboids', compensatedCuboids);
      this.cloud.material.setUniform(
        'compensatedCuboidCount',
        compensatedCuboidCount,
      );
    }
  }

  private setDensity(state: PointCloudContentState) {
    const currentFrameNumber = state.render.frameNumber;
    const density = state.render.enhancedCloudSettings.density;

    const pointCount = this.cloud.geometry.getAttribute('position').count;
    const drawEnd = Math.floor((pointCount * density) / 100);
    const isCurrentFrame = currentFrameNumber !== this.frameNumber;

    this.cloud.geometry.setDrawRange(0, isCurrentFrame ? drawEnd : Infinity);
  }

  private setPointSize(state: PointCloudContentState) {
    const isCurrent = state.render.frameNumber === this.frameNumber;
    const pointSize = isCurrent ? state.render.layerSettings.pointSize : 1;
    this.cloud.material.setUniform('pointSize', pointSize);
  }

  private setHideGround(hideGround: boolean) {
    this.cloud.material.setUniform('hideGround', hideGround);
  }

  private setViewBoxHighlight(config: ConfigureMeshesConfiguration) {
    const pointCloudOutputName = config.state.output.name;
    if (config.outputName !== pointCloudOutputName) {
      // reset view box uniforms for non-point-cloud output
      setViewBoxHighlightUniforms(this.cloud.material);
      return;
    }

    if (!isRenderingWithViewBoxHighlight(config.state)) {
      assignProjectionDefines(this.cloud.material, 'pinhole');
      // reset view box uniforms for non-point-cloud output
      setViewBoxHighlightUniforms(this.cloud.material);
      return;
    }

    updateSplitViewOutputForHighlight(
      this.cloud.material,
      config.state,
      this.splitViewCamera,
    );

    updateMaterialForHighlight(
      this.cloud.material,
      config.state,
      this.splitViewCamera,
    );
  }

  private setRegionOfInterest(config: ConfigureMeshesConfiguration) {
    if (!isRenderingWithRegionOfInterest(config.state, config.outputName)) {
      setRegionOfInterestUniforms(this.cloud.material);
      return;
    }
    setRegionOfInterestUniforms(this.cloud.material, config.state);
  }
}
